// Voice/video call REST API. The socket carries the ringing events; these endpoints
// drive the call lifecycle and mint the LiveKit join tokens.
import axios from "axios";
import { store } from "../store";
import { logout } from "../slices/authSlice";
import type { CallRecord, CallSession, StartCallBody } from "../../types/call";

const callAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000/api",
  timeout: 60000,
});

callAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

callAxios.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.status === 401) {
      store.dispatch(logout());
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

const unwrap = <T,>(res: any): T => (res?.data?.data as T) ?? (undefined as unknown as T);

const BASE = "/calls";

export async function startCall(body: StartCallBody): Promise<CallSession> {
  const res = await callAxios.post(`${BASE}/start`, body);
  return unwrap(res);
}

export async function acceptCall(callId: number): Promise<CallSession> {
  const res = await callAxios.post(`${BASE}/${callId}/accept`);
  return unwrap(res);
}

export async function declineCall(callId: number): Promise<void> {
  await callAxios.post(`${BASE}/${callId}/decline`);
}

// Rejoin an ongoing call (from the chat note).
export async function joinCall(callId: number): Promise<CallSession> {
  const res = await callAxios.post(`${BASE}/${callId}/join`);
  return unwrap(res);
}

// Redial a past call — rings everyone who was on it again.
export async function redialCall(callId: number, callType?: "voice" | "video"): Promise<CallSession> {
  const res = await callAxios.post(`${BASE}/${callId}/redial`, callType ? { callType } : {});
  return unwrap(res);
}

export async function cancelCall(callId: number): Promise<void> {
  await callAxios.post(`${BASE}/${callId}/cancel`);
}

export async function endCall(callId: number): Promise<void> {
  await callAxios.post(`${BASE}/${callId}/end`);
}

// Add one person to an ongoing call (caller only, enforced server-side).
export async function addToCall(
  callId: number,
  body: { targetType: "retailer" | "sales"; targetId: number }
): Promise<{ call: CallRecord }> {
  const res = await callAxios.post(`${BASE}/${callId}/add`, body);
  return unwrap(res);
}

// Caller-only moderation.
export async function muteCallParticipant(
  callId: number,
  identity: string,
  muted: boolean
): Promise<void> {
  await callAxios.post(`${BASE}/${callId}/mute`, { identity, muted });
}

export async function kickCallParticipant(callId: number, identity: string): Promise<void> {
  await callAxios.post(`${BASE}/${callId}/kick`, { identity });
}

export async function getCallHistory(): Promise<CallRecord[]> {
  const res = await callAxios.get(`${BASE}/history`);
  return unwrap(res) ?? [];
}
